import { useEffect } from "react";
import { invoke } from "@tauri-apps/api/core";
import { getCurrentWindow } from "@tauri-apps/api/window";
import { useDesktopMode } from "./use-desktop-mode";
import { getWindowDisplayId, getWindowRoleSync } from "./use-window-role";
import { useActiveDisplay } from "./use-active-display";
import { isThisWindowActive } from "./use-active-window";
import { track } from "./observability";
import { listenWhileMounted } from "./tauri-listener-cleanup";
import { guideAllowsShortcut } from "./onboarding/quick-guide/guide-shortcut-gate";
import type { WindowOpener } from "./window-opener";

const dbg = (msg: string) => {
  void invoke("dbg_log", { msg }).catch(() => {});
};

type HotkeyDisplayPayload = {
  source?: "hotkey";
  displayId?: number | null;
  x?: number;
  y?: number;
};

/** Bring this overlay back; only the person's open also gives it the keyboard. */
function restoreCurrentWindow(opener: WindowOpener) {
  const win = getCurrentWindow();
  const restore = () =>
    invoke("restore_desktop_window", { opener }).catch(async () => {
      // `show` and `unminimize` activate the window, so only the person's
      // open may fall back to them.
      if (opener !== "user") return;
      await win.show();
      await win.unminimize();
    });
  const settle = () =>
    opener === "user" ? restore().then(() => win.setFocus()) : restore();
  void settle().catch(() => {});
  window.setTimeout(() => {
    void settle().catch(() => {});
  }, 120);
}

/** The person opened something here (a click, a shortcut, a menu). */
export function restoreAndFocusCurrentWindow() {
  restoreCurrentWindow("user");
}

/** An agent, or Denker by itself, opened something here: show it without
 *  taking the keyboard from what the person is typing in. */
export function revealCurrentWindow() {
  restoreCurrentWindow("denker");
}

export const OPEN_WORKSPACE_ACTIVE_DISPLAY_EVENT =
  "denker://open-workspace-active-display";

/**
 * Global Cmd+D handler.
 *
 * Phase 5: Cmd+D detection moved to Rust (`hotkeys.rs` CGEventTap)
 * because `tauri-plugin-global-shortcut` registers a system-global
 * shortcut that races across N webviews when multi-display is in
 * play — every webview's `register` call wins/loses non-deterministically
 * and the shortcut fires on whichever webview happened to win the
 * registration race. The Rust tap fires once per chord and emits a
 * single `toggle-workspace` event; every webview listens, but only
 * the active-display window acts on it.
 *
 * The Rust side also funnels the tray "Show / Hide Workspace"
 * menu click through the same event, so the JS gating logic below
 * applies to both code paths.
 */
export function useGlobalHotkey() {
  useEffect(() => {
    dbg(
      `useGlobalHotkey mount role=${getWindowRoleSync()} displayId=${getWindowDisplayId()}`,
    );

    const isActiveDisplay = (payload?: HotkeyDisplayPayload | null) => {
      const myDisplayId = getWindowDisplayId();
      const isHotkey = payload?.source === "hotkey";
      const payloadDisplayId =
        typeof payload?.displayId === "number" ? payload.displayId : null;
      if (
        payloadDisplayId !== null &&
        typeof payload?.x === "number" &&
        typeof payload?.y === "number"
      ) {
        useActiveDisplay.setState({
          activeDisplayId: payloadDisplayId,
          cursorX: payload.x,
          cursorY: payload.y,
        });
      }
      const activeDisplayId =
        payloadDisplayId ?? useActiveDisplay.getState().activeDisplayId;
      const isActive =
        payloadDisplayId !== null
          ? myDisplayId === payloadDisplayId
          : isHotkey
            ? false
            : isThisWindowActive();
      return { myDisplayId, activeDisplayId, isActive };
    };

    const stopToggle = listenWhileMounted<HotkeyDisplayPayload | null>(
      "toggle-workspace",
      (event) => {
        if (!guideAllowsShortcut("shortcut.toggle_workspace")) return;
        const { myDisplayId, activeDisplayId, isActive } = isActiveDisplay(
          event?.payload ?? null,
        );
        const mode = useDesktopMode.getState().mode;
        dbg(
          `toggle-workspace received role=${getWindowRoleSync()} myDisplay=${myDisplayId} activeDisplay=${activeDisplayId} isActive=${isActive} mode=${mode}`,
        );
        if (!isActive) return;
        track("desktop_hotkey_summoned", {
          hotkey: "cmd_d",
          from_mode: mode,
        });
        // Restore focus around the transition so the hotkey also recovers when
        // macOS has left another application frontmost.
        restoreAndFocusCurrentWindow();
        useDesktopMode.getState().toggleWorkspace();
        restoreAndFocusCurrentWindow();
        dbg(
          `toggle-workspace acted; new mode=${useDesktopMode.getState().mode}`,
        );
      },
    );

    const stopOpen = listenWhileMounted<HotkeyDisplayPayload | null>(
      OPEN_WORKSPACE_ACTIVE_DISPLAY_EVENT,
      (event) => {
        const { myDisplayId, activeDisplayId, isActive } = isActiveDisplay(
          event?.payload ?? null,
        );
        const mode = useDesktopMode.getState().mode;
        dbg(
          `open-workspace-active-display received role=${getWindowRoleSync()} myDisplay=${myDisplayId} activeDisplay=${activeDisplayId} isActive=${isActive} mode=${mode}`,
        );
        if (!isActive) return;
        track("desktop_hotkey_summoned", {
          hotkey: "onboarding_open_workspace",
          from_mode: mode,
        });
        restoreAndFocusCurrentWindow();
        useDesktopMode.getState().openWorkspace();
        restoreAndFocusCurrentWindow();
        dbg(
          `open-workspace-active-display acted; new mode=${useDesktopMode.getState().mode}`,
        );
      },
    );

    return () => {
      stopToggle();
      stopOpen();
    };
  }, []);
}
